/* eslint-disable max-lines */
import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { computed, effect, inject, Signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { DateUtils, PreconisationInformationDTO, ResultatsCalculDTO, Simulation, TypeModalite } from "@mra/hlp9002";
import { tapResponse } from "@ngrx/operators";
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { Observable, of, switchMap } from "rxjs";
import { Beneficiaire } from "src/app/models/beneficiaire.model";
import { ChoixPerception, PreconisationModifiee } from "src/app/models/preconisationModifiee.model";
import { RenteCalculService } from "src/app/shared/services/rente/rente-calcul.service";
import { AffichageInfos } from "../models/affichageInfos";
import { ChoixSimuConso, PrecoMapperService } from "../pages/parcours-simulation/ma-simulation/prest-simulateur-conso/preco-mapper.service";
import { LocalStorageService, StateKey } from "../shared/services/local-storage.service";
import { PrecoInformationsService } from "../shared/services/preconisation/preco-informations.service";
import { waitForUserInitialized } from "../utils/rxjs-utils";
import { SimuUtils } from "../utils/simu-utils";
import { errorsStore } from "./errors.store";
import { questionnaireStore } from "./questionnaire.store";
import { EntreeHistoriqueOption, RenteState, SavedRenteState } from "./state";
import { userStore } from "./user.store";

const LIMITE_HISTORIQUE: number = 500;

function createDefaultState(): RenteState {
    return {
        renteSansOpt: null,
        renteAvecOpt: null,

        preconisation: null,
        precoModifiee: null,
        historiqueConso: [],

        selectedOptionsRente: new Map<TypeModalite, string>(),
        historiqueOptions: [],
        derniereOptionModifiee: null,

        beneficiaire: null,
        clauseBeneficiaireCode: null
    };
}

export type RenteSansOptSignalData = {
    dateEffet: string;
    consommationCapital: number;
    renteSansOption: ResultatsCalculDTO;
    consommationRente: number;
};

// eslint-disable-next-line @typescript-eslint/typedef
export const renteStore = signalStore(
    { providedIn: "root" },
    withDevtools("rente"),
    withState(createDefaultState()),
    withComputed((store) => {
        const user: InstanceType<typeof userStore> = inject(userStore);

        return {
            montantRenteAvecOpt: computed(() => store.renteAvecOpt()?.montantTotal),
            precoCapital: computed(() => store.preconisation()?.sortieCapital),
            modalitesPreconisees: computed(() => store.preconisation()?.renteModalites ?? []),
            codesModalites: computed(() => {
                if (store.selectedOptionsRente().size === 0) {
                    return store.preconisation()?.renteModalites?.map((mod) => mod.code) ?? ([] as string[]);
                }
                return Array.from(store.selectedOptionsRente()?.entries()).map((entry) => entry[1]);
            }),

            consommationRente: computed(() => {
                return store.precoModifiee()?.consommationRente ?? store.preconisation()?.consommationRente ?? user.epargneCumulee() ?? 0;
            }),

            consommationCapital: computed(() => {
                return store.precoModifiee()?.consommationCapital ?? 0;
            }),

            consommationRpp: computed(() => {
                return store.precoModifiee()?.consommationRpp ?? 0;
            })
        };
    }),

    // Deuxième withComputed pour pouvoir utiliser les autres computed
    withComputed((store) => {
        const user: InstanceType<typeof userStore> = inject(userStore);

        return {
            consommationCagnotte: computed(() => Math.max(0, user.epargneCumulee() - store.consommationRente() - store.consommationCapital() - store.consommationRpp()))
        };
    }),

    withMethods(
        (
            store,
            renteService = inject(RenteCalculService),
            preconisationService = inject(PrecoInformationsService),
            errorStore = inject(errorsStore),
            user = inject(userStore),
            questionnaire = inject(questionnaireStore),
            precoMapper = inject(PrecoMapperService)
        ) => {
            function dateEffet() {
                const configUti: PreconisationModifiee = store.precoModifiee();
                const simu: Simulation = user.simulation();
                if (!simu) return null;
                return SimuUtils.getDateEffet(configUti, simu);
            }
            return {
                unsetSimulation(): void {
                    patchState(store, () => createDefaultState());
                },

                retrieveState(savedState: SavedRenteState): void {
                    patchState(store, () => {
                        if (savedState) {
                            savedState.selectedOptionsRente = new Map(Object.entries(savedState.selectedOptionsRente));
                        }
                        savedState.historiqueConso?.map((changement) => {
                            changement.dateEffet = DateUtils._(changement.dateEffet?.toString().split("T")[0]);
                        });
                        return savedState ?? createDefaultState();
                    });
                },

                dateEffet: computed(() => dateEffet()),

                getPreconisationModifiee: computed(() => {
                    const existing: PreconisationModifiee = store.precoModifiee();
                    if (existing) return existing;
                    const simu: Simulation = user.simulation();
                    const dateEffet: string = SimuUtils.getDateEffet(existing, simu);
                    const originalPreco: PreconisationInformationDTO = store?.preconisation();
                    return originalPreco
                        ? {
                              ...originalPreco,
                              dateEffet,
                              choixPerception: ChoixPerception.Annuel
                          }
                        : null;
                }),

                selectionnerOption(type: TypeModalite, value: string): void {
                    patchState(store, (state) => {
                        if (!type || !value) return state;
                        const newState: RenteState = { ...state };
                        // Snapshot complet de l'état AVANT le changement, comme historiqueConso stocke l'état courant avant modification.
                        newState.historiqueOptions = [
                            ...state.historiqueOptions,
                            {
                                type,
                                value,
                                snapshot: state.selectedOptionsRente
                            }
                        ];
                        if (newState.historiqueOptions.length > LIMITE_HISTORIQUE) {
                            newState.historiqueOptions.shift();
                        }
                        newState.selectedOptionsRente = new Map(state.selectedOptionsRente).set(type, value);
                        newState.derniereOptionModifiee = type;
                        return newState;
                    });
                },

                /**
                 * Applique en une seule passe la sélection renvoyée par le backend, sans pousser dans l'historique.
                 * No-op si toutes les valeurs correspondent déjà au store, ce qui évite de ré-invalider
                 * codesModalites et de déclencher un double-appel MS0203.
                 */
                appliquerSelectionBackend(selection: Partial<Record<TypeModalite, string>>): void {
                    const current: Map<TypeModalite, string> = store.selectedOptionsRente();
                    const hasChanges: boolean = Object.entries(selection).some(([type, code]) => current.get(type as TypeModalite) !== code);
                    if (!hasChanges) return;
                    patchState(store, (state) => {
                        const newMap: Map<TypeModalite, string> = new Map(state.selectedOptionsRente);
                        for (const [type, code] of Object.entries(selection)) {
                            newMap.set(type as TypeModalite, code);
                        }
                        return { selectedOptionsRente: newMap };
                    });
                },

                clearDerniereOptionModifiee(): void {
                    patchState(store, () => ({ derniereOptionModifiee: null as TypeModalite | null }));
                },

                precoChoixOptionPrecedent(): Map<TypeModalite, string> {
                    const historique: EntreeHistoriqueOption[] = store.historiqueOptions();
                    const snapshot: Map<TypeModalite, string> = historique.length > 0 ? historique[historique.length - 1]?.snapshot : null;
                    if (!snapshot) return null;
                    patchState(store, (state) => {
                        if (!state.historiqueOptions.length) return state;
                        const newHistorique: EntreeHistoriqueOption[] = [...state.historiqueOptions];
                        newHistorique.pop();
                        return {
                            ...state,
                            historiqueOptions: newHistorique,
                            selectedOptionsRente: new Map(snapshot)
                        };
                    });
                    return snapshot;
                },

                precoChoixConsoPrecedent(): Partial<ChoixSimuConso> {
                    const historiqueConso: Partial<ChoixSimuConso>[] = store.historiqueConso();
                    const prevChangement: Partial<ChoixSimuConso> = historiqueConso?.length > 1 ? historiqueConso[historiqueConso.length - 2] : null;
                    patchState(store, (state) => {
                        if ((state.historiqueConso?.length ?? 0) < 2) return state;
                        const newHistorique: Partial<ChoixSimuConso>[] = [...state.historiqueConso];
                        newHistorique.pop();
                        return {
                            ...state,
                            historiqueConso: newHistorique
                        };
                    });
                    return prevChangement;
                },

                reinitListeOptions(): void {
                    patchState(store, () => {
                        const selectedOptionsRente: Map<TypeModalite, string> = store
                            .preconisation()
                            ?.renteModalites?.reduce((acc, value) => acc.set(value.type, value.code), new Map<TypeModalite, string>());
                        return { selectedOptionsRente };
                    });
                },

                resetPrecoToOriginal(): void {
                    patchState(store, (state) => ({
                        precoModifiee: {
                            ...state.precoModifiee,
                            ...state.preconisation
                        }
                    }));
                },

                ajouterHistoriqueSimConso(changement: Partial<ChoixSimuConso>): void {
                    patchState(store, (state) => {
                        const newHistoConso: Partial<ChoixSimuConso>[] = [...(state.historiqueConso ?? []), changement];
                        if (newHistoConso.length > 500) {
                            newHistoConso.shift();
                        }
                        return { historiqueConso: newHistoConso };
                    });
                },

                supprimerPreconisation(): void {
                    patchState(store, () => ({
                        preconisation: null as PreconisationInformationDTO,
                        precoModifiee: null as PreconisationModifiee
                    }));
                },

                loadPreconisation: (): Observable<PreconisationInformationDTO> =>
                    preconisationService.getPreconisation(user.epargneCumulee(), dateEffet(), questionnaire.typeParcours(), questionnaire.getAllResponses()).pipe(
                        tapResponse({
                            next: (preconisation) => {
                                const selectedOptionsRente: Map<TypeModalite, string> = preconisation.renteModalites.reduce(
                                    (acc, value) => acc.set(value.type, value.code),
                                    new Map<TypeModalite, string>()
                                );
                                const affichageInfos: AffichageInfos = questionnaire.affichageInfos();
                                questionnaire.setAffichageInfos({
                                    afficher: affichageInfos?.afficher,
                                    pageCourante: 0,
                                    ecrans: preconisation.universEcrans
                                });
                                patchState(store, () => ({
                                    preconisation,
                                    selectedOptionsRente,
                                    historiqueConso: [
                                        precoMapper.precoToChoixSimu({
                                            ...preconisation,
                                            dateEffet: dateEffet(),
                                            choixPerception: ChoixPerception.Annuel
                                        })
                                    ] as Partial<ChoixSimuConso>[]
                                }));
                            },
                            error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente préconisée`)
                        })
                    ),

                setPreconisation(modification: PreconisationModifiee): void {
                    patchState(store, () => ({ precoModifiee: modification }));
                },

                setBeneficiaire(beneficiaire: Beneficiaire, clauseCode: string = null): void {
                    patchState(store, (state) => ({
                        beneficiaire: beneficiaire ?? state.beneficiaire,
                        clauseBeneficiaireCode: clauseCode ?? state.clauseBeneficiaireCode
                    }));
                },

                createRenteSansOptResource: (signal?: Signal<Partial<RenteSansOptSignalData>>) =>
                    rxResource({
                        params: computed(() => ({
                            dateEffet: dateEffet(),
                            consommationCapital: store.consommationCapital(),
                            renteSansOption: store.renteSansOpt(),
                            consommationRente: store.consommationRente() ?? 0,
                            ...signal?.()
                        })),
                        stream: (loaderParams) => {
                            const params: RenteSansOptSignalData = loaderParams.params;
                            if (params.renteSansOption) return of(params.renteSansOption);
                            return waitForUserInitialized(user).pipe(
                                switchMap(() =>
                                    renteService.renteCalcul(params.dateEffet, params.consommationRente, params.consommationCapital, store.beneficiaire()).pipe(
                                        tapResponse({
                                            next: (renteSansOpt) => patchState(store, () => ({ renteSansOpt })),
                                            error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente`)
                                        })
                                    )
                                )
                            );
                        }
                    }),

                createRenteAvecOptResource: (
                    signal: Signal<{
                        options: string[];
                        consoRente: number;
                    }> = null
                ) =>
                    rxResource({
                        params:
                            signal ??
                            computed(() => ({
                                options: Array.from(store.selectedOptionsRente().values()).filter(Boolean),
                                consoRente: store.consommationRente()
                            })),
                        stream: (loaderParams) =>
                            renteService
                                .renteCalcul(dateEffet(), loaderParams.params.consoRente, store.consommationCapital(), store.beneficiaire(), true, loaderParams.params.options)
                                .pipe(
                                    tapResponse({
                                        next: (renteAvecOpt) => patchState(store, () => ({ renteAvecOpt })),
                                        error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente avec options`)
                                    })
                                )
                    })
            };
        }
    ),

    withHooks((store) => {
        const localStorageService: LocalStorageService = inject(LocalStorageService);

        return {
            onInit() {
                const state: RenteState = localStorageService.getSavedState<RenteState>(StateKey.RENTE);
                if (state) store.retrieveState(state);
                effect(() => {
                    const state: RenteState = getState(store);
                    const savedState: SavedRenteState = {
                        ...state,
                        selectedOptionsRente: Object.fromEntries(state.selectedOptionsRente)
                    };
                    localStorageService.saveState(StateKey.RENTE, savedState);
                });
            }
        };
    })
);
